import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Icon from 'src/components/universe/Icon';
import useSafeTranslation from 'src/hooks/useSafeTranslation';
import useNotificationInbox from './useNotificationInbox';
import PushSettings from './PushSettings';
import { accentOf, accentTint } from 'src/theme/universe/brand';
import { iconButtonSx } from 'src/theme/universe/buttonSx';
import {
  notificationContentSx,
  notificationPaperSx
} from './notificationStyles';

export default function NotificationBell() {
  const { t, i18n } = useSafeTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [settings, setSettings] = useState(false);
  const open = Boolean(anchor);
  const inbox = useNotificationInbox(open);
  const close = () => setAnchor(null);
  const title = t('notifications.title', { defaultValue: 'Notifiche' });
  const content = (
    <Box
      sx={{
        ...notificationContentSx(theme),
        display: 'flex',
        flexDirection: 'column',
        maxHeight: mobile ? '100dvh' : 'min(80vh, 720px)',
        height: mobile ? '100%' : 'auto'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <Typography id="notification-panel-title" variant="h6">
          {title}
        </Typography>
        <IconButton
          onClick={close}
          aria-label={t('notifications.close', {
            defaultValue: 'Chiudi notifiche'
          })}
        >
          <Icon name="CloseCircle" size="md" />
        </IconButton>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ px: 1, pb: 1 }}
      >
        <Button
          size="small"
          onClick={() => inbox.markRead()}
          disabled={!inbox.count}
        >
          {t('notifications.readAll', {
            defaultValue: 'Segna tutte come lette'
          })}
        </Button>
        <Button
          size="small"
          onClick={() => setSettings((value) => !value)}
          aria-expanded={settings}
        >
          {t('notifications.push.button', { defaultValue: 'Notifiche push' })}
        </Button>
      </Stack>
      <Divider />
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {inbox.error && (
          <Alert
            severity="error"
            action={
              <Button onClick={inbox.refresh}>
                {t('notifications.retry', { defaultValue: 'Riprova' })}
              </Button>
            }
          >
            {t('notifications.loadError', {
              defaultValue: 'Impossibile caricare le notifiche.'
            })}
          </Alert>
        )}
        {inbox.realtimeError && (
          <Alert severity="warning">
            {t('notifications.realtimeError', {
              defaultValue:
                'Aggiornamento in tempo reale non disponibile. Riapri il pannello per aggiornare.'
            })}
          </Alert>
        )}
        {!inbox.loading && !inbox.error && !inbox.items.length && (
          <Stack spacing={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1">
              {t('notifications.empty', { defaultValue: 'Nessuna notifica' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.emptyHelp', {
                defaultValue:
                  'Qui troverai le conferme delle allocation dei tuoi progetti.'
              })}
            </Typography>
          </Stack>
        )}
        <List disablePadding>
          {inbox.items.map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              divider
              sx={{
                alignItems: 'stretch',
                bgcolor: item.readAt
                  ? 'transparent'
                  : accentTint(theme, 'hover')
              }}
            >
              <ListItemButton
                alignItems="flex-start"
                onClick={() => {
                  close();
                  navigate(`/dashboard/notifications/${item.id}`);
                }}
                sx={{ p: 2 }}
              >
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {!item.readAt && (
                      <Box
                        component="span"
                        aria-label={t('notifications.unread', {
                          defaultValue: 'Non letta'
                        })}
                        sx={{
                          width: theme.spacing(1),
                          height: theme.spacing(1),
                          borderRadius: '50%',
                          bgcolor: accentOf(theme),
                          flexShrink: 0
                        }}
                      />
                    )}
                    <Typography variant="subtitle2">{item.title}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {item.body}
                  </Typography>
                  <Typography variant="caption" sx={{ color: accentOf(theme) }}>
                    {item.project?.number}
                  </Typography>
                  <Typography
                    component="time"
                    dateTime={item.createdAt}
                    variant="caption"
                    color="text.secondary"
                  >
                    {new Date(item.createdAt).toLocaleString(
                      i18n?.resolvedLanguage || 'it-IT',
                      { dateStyle: 'short', timeStyle: 'short' }
                    )}
                  </Typography>
                </Stack>
              </ListItemButton>
              {!item.readAt && (
                <Tooltip
                  title={t('notifications.read', {
                    defaultValue: 'Segna come letta'
                  })}
                >
                  <IconButton
                    sx={{ alignSelf: 'center', mr: 1 }}
                    onClick={() => inbox.markRead(item.id)}
                    aria-label={t('notifications.readItem', {
                      defaultValue: 'Segna come letta: {{title}}',
                      title: item.body
                    })}
                  >
                    <Icon name="TickCircle" size="md" />
                  </IconButton>
                </Tooltip>
              )}
            </ListItem>
          ))}
        </List>
        {inbox.loading && (
          <Box
            role="status"
            aria-label={t('notifications.loading', {
              defaultValue: 'Caricamento notifiche'
            })}
            sx={{ p: 2, textAlign: 'center' }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
        {inbox.nextCursor && (
          <Button
            fullWidth
            disabled={inbox.loading}
            onClick={() => inbox.load(inbox.nextCursor)}
            sx={{ my: 1 }}
          >
            {t('notifications.more', {
              defaultValue: 'Carica altre notifiche'
            })}
          </Button>
        )}
        {settings && <PushSettings />}
      </Box>
    </Box>
  );
  return (
    <>
      <Tooltip title={title}>
        <IconButton
          sx={iconButtonSx(theme)}
          aria-label={t('notifications.bellLabel', {
            defaultValue: 'Notifiche, {{count}} non lette',
            count: inbox.count
          })}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <Badge badgeContent={inbox.count} color="error" max={99}>
            <Icon name="Notification" size="md" />
          </Badge>
        </IconButton>
      </Tooltip>
      {mobile ? (
        <Dialog
          fullScreen
          open={open}
          onClose={close}
          aria-labelledby="notification-panel-title"
        >
          {open && content}
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            role: 'dialog',
            'aria-labelledby': 'notification-panel-title',
            sx: notificationPaperSx
          }}
        >
          {open && content}
        </Popover>
      )}
    </>
  );
}
